import React from 'react';
import { Star, Users, TrendingUp, Calendar, CheckCheck, MessageCircle, Award } from 'lucide-react';

const ProfileHeader = ({ artist, agents = [], influencers = [], totalPerformances = 0 }) => {
  const primaryAgent = agents?.find(a => a?.is_primary) || agents?.[0];
  const totalInfluencerEngagement = influencers?.reduce((sum, inf) => sum + (inf?.engagement_score || 0), 0) || 0;
  const avgEngagement = influencers?.length > 0 ? (totalInfluencerEngagement / influencers?.length)?.toFixed(1) : 0;

  return (
    <div className="relative">
      {/* Cover Image */}
      <div className="h-64 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500">
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
      </div>
      {/* Profile Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-end sm:space-x-5 -mt-12 sm:-mt-16">
          {/* Profile Image */}
          <div className="flex">
            <img
              className="h-24 w-24 sm:h-32 sm:w-32 rounded-full ring-4 ring-white bg-white"
              src={artist?.user_profiles?.avatar_url || `/api/placeholder/128/128`}
              alt={artist?.user_profiles?.full_name}
            />
          </div>

          {/* Profile Info */}
          <div className="mt-6 sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
            <div className="sm:hidden 2xl:block mt-6 min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-white truncate">
                {artist?.business_name || artist?.user_profiles?.full_name}
              </h1>
              <p className="text-white text-opacity-90">
                {artist?.description?.substring(0, 100) || 'Artiste professionnel'}{artist?.description?.length > 100 ? '...' : ''}
              </p>
            </div>

            <div className="mt-6 flex flex-col justify-stretch space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4">
              <button
                type="button"
                className="inline-flex justify-center px-4 py-2 border border-white text-sm font-medium rounded-md text-white hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <MessageCircle className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />
                Contacter
              </button>
              <button
                type="button"
                className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-purple-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Réserver
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Profile Info */}
        <div className="hidden sm:block 2xl:hidden mt-6 min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-white truncate">
            {artist?.business_name || artist?.user_profiles?.full_name}
          </h1>
          <p className="text-white text-opacity-90">
            {artist?.description?.substring(0, 100) || 'Artiste professionnel'}{artist?.description?.length > 100 ? '...' : ''}
          </p>
        </div>
      </div>
      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Left Stats */}
            <div className="flex items-center space-x-6">
              {/* Rating */}
              <div className="flex items-center">
                <Star className="w-5 h-5 text-yellow-400 fill-current" />
                <span className="ml-1 text-sm font-medium text-gray-900">
                  {artist?.average_rating > 0 ? artist?.average_rating : 'Nouveau'}
                </span>
                <span className="ml-1 text-sm text-gray-500">
                  ({artist?.total_bookings || 0} avis)
                </span>
              </div>

              {/* Verification */}
              {artist?.is_verified && (
                <div className="flex items-center">
                  <CheckCheck className="w-5 h-5 text-blue-500" />
                  <span className="ml-1 text-sm font-medium text-blue-600">Vérifié</span>
                </div>
              )}

              {/* Awards */}
              {artist?.total_bookings > 10 && (
                <div className="flex items-center">
                  <Award className="w-5 h-5 text-purple-500" />
                  <span className="ml-1 text-sm font-medium text-purple-600">Artiste populaire</span>
                </div>
              )}
            </div>

            {/* Right Stats */}
            <div className="flex items-center space-x-6">
              {/* Agents */}
              {agents?.length > 0 && (
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-gray-400" />
                  <span className="ml-1 text-sm text-gray-600">
                    {agents?.length} agent{agents?.length > 1 ? 's' : ''}
                  </span>
                  {primaryAgent && (
                    <span className="ml-1 text-sm text-gray-500">
                      • Principal: {primaryAgent?.agent?.user_profiles?.full_name}
                    </span>
                  )}
                </div>
              )}

              {/* Influencers */}
              {influencers?.length > 0 && (
                <div className="flex items-center">
                  <TrendingUp className="w-5 h-5 text-gray-400" />
                  <span className="ml-1 text-sm text-gray-600">
                    {influencers?.length} influenceur{influencers?.length > 1 ? 's' : ''}
                  </span>
                  {avgEngagement > 0 && (
                    <span className="ml-1 text-sm text-gray-500">
                      • {avgEngagement}% engagement
                    </span>
                  )}
                </div>
              )}

              {/* Performances */}
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="ml-1 text-sm text-gray-600">
                  {totalPerformances} performance{totalPerformances > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;